/**
 * 手机端「页面死亡 → 浏览器自动恢复 → 再死」循环的自适应护栏。
 *
 * 2026-09-28 实测（laomendong2/meihuashan，两台 iPhone，微信与 Safari 皆复现）：
 * 重场景流式加载期间 WebKit 内容进程死亡，浏览器整页恢复 reload——每轮都是
 * 全新 boot + 全量进房，用户侧表现为「进场景几秒 → 回到加载界面」循环。死因
 * 在页面进程内部，任何 in-page 重连机制都无能为力（RoomSession 的 15s 窗口
 * 全程未触发即被整页死亡绕过），客户端唯一能做的是把本次 boot 的资源峰值压
 * 下来。sessionStorage 跨 reload 存活、按 tab 隔离，恰好就是崩溃恢复的语义。
 *
 * 降档分级（markBoot 快照，档位见 createApp/loadGaussianScene 各使用点）：
 *  tier 2（全面降档）——任一命中：
 *   ① boot 环（紧凑循环）：90s 内 ≥3 次 boot——几秒一死的经典循环；
 *   ② 崩溃证据：10 分钟内 ≥2 次崩溃恢复——死过不止一次，全面降档；
 *   ③ 场景级（确定性信号）：manifest.mobileLowSpec 声明的重载场景（桌面级
 *     LOD 树、无手机减量资产，如 meihuashan 784MB）手机端无条件降档——首死
 *     之前就该避开，不依赖任何检测。
 *  tier 1（降一级）——10 分钟内恰 1 次崩溃恢复：用户拍板「被系统杀掉重启
 *   自动把 LOD 精度降一级」，温和回应单次死亡（LOD 上限 1→2 + 预算 2M→1.2M，
 *   即 2026-09-22 实测过的帧率修复组合），再死才进 tier 2。ring 不参与 tier 1
 *   （手动连刷两次刷新不该降档）。
 *
 * 崩溃恢复识别（tier 1/2 的证据来源）：boot 时发现上个会话「有 boot 标记、无
 * 干净退出标记（pagehide 会写）、心跳新鲜（3 分钟内还在跑）」⇒ 页面是被系统
 * 杀掉的，记入崩溃时间戳。当夜实测暴露 boot 环的死角：传送 meihuashan 后
 * 「走 40-75s 才死」的节奏下 ring 永远卡在 2（第三个 boot 出现时第一个已滚出
 * 90s 窗口），TRIGGER=3 永远打不着——稀疏死亡必须靠「死过」这个事实本身，
 * 而不是死亡凑节奏。
 *
 * 会话稳定存活满 STABLE_SESSION_MS 后清空 boot 环并放宽 tier（下次 boot 恢复
 * 标准档）；崩溃时间戳不受影响，10 分钟自然过期——错杀（罕见误判一次降档
 * 画质）代价远小于漏杀（用户整个会话循环死亡）。
 */
const KEY = 'vr-boot-times';
const RING = 6; // 保留最近 N 次 boot 时间戳
const WINDOW_MS = 90_000; // 计数窗口：90s 内 …
const TRIGGER = 3; // … ≥3 次 boot 即判定死亡循环（手动刷新到这个频率也该降档）
/** 崩溃证据记忆窗口：窗口内有崩溃恢复记录 ⇒ 降档（稀疏死亡循环的兜底）。 */
const CRASH_WINDOW_MS = 600_000;
/** 心跳新鲜度上限：boot 时距上次心跳超过该值不判崩溃（用户正常关掉隔天再开）。 */
const CRASH_ALIVE_FRESH_MS = 180_000;
/** 崩溃恢复识别用 sessionStorage 键（见 markBoot/markAlive/markCleanExit）。 */
const KEY_BOOTED = 'vr-booted';
const KEY_CLEAN = 'vr-clean-exit';
const KEY_ALIVE = 'vr-alive-at';
const KEY_CRASHES = 'vr-crashes';
/** 会话存活满此时长即视为稳定，清空 boot 环（下次 boot 恢复标准档）。 */
export const STABLE_SESSION_MS = 120_000;

/** markBoot 判定的 boot 降档级别快照（0/1/2）。 */
let bootTier = 0;
/** 场景级降档（manifest.mobileLowSpec）：与 boot 降档共用档位，切场景时设置。 */
let sceneLowSpec = false;

function readList(key: string): number[] {
    try {
        return JSON.parse(sessionStorage.getItem(key) ?? '[]');
    } catch {
        return [];
    }
}

function writeList(key: string, list: number[]): void {
    try {
        sessionStorage.setItem(key, JSON.stringify(list));
    } catch {
        /* 隐私模式/配额异常时按标准档走 */
    }
}

/** boot 时调用一次（main() 第一行，早于任何 await）：记录时间戳、识别崩溃
 * 恢复、判定本会话的降档级别。必须在 bootstrapBusiness/createApp 之前——
 * 极速死亡循环（实测 Android WebView 曾每轮 <3s、死在 entry 请求之前）也
 * 要来得及计数。 */
export function markBoot(): void {
    bootTier = 0;
    try {
        const now = Date.now();
        // 崩溃恢复识别：上个会话 boot 过、没留下干净退出标记、且心跳新鲜
        // （分钟级内页面还在跑）⇒ 不是正常关闭/跳转，是页面死亡。
        const booted = sessionStorage.getItem(KEY_BOOTED) === '1';
        const clean = sessionStorage.getItem(KEY_CLEAN) === '1';
        const aliveAt = Number(sessionStorage.getItem(KEY_ALIVE) ?? 0);
        const crashNow = booted && !clean && aliveAt > 0 && now - aliveAt < CRASH_ALIVE_FRESH_MS;
        sessionStorage.removeItem(KEY_CLEAN);
        sessionStorage.setItem(KEY_BOOTED, '1');
        // 心跳从 boot 起即有效（不等首个 5s 定时）：本轮若在首个心跳前死亡，
        // 下次 boot 也能凭本时间戳判崩溃。
        sessionStorage.setItem(KEY_ALIVE, String(now));

        const crashes = readList(KEY_CRASHES).filter((t) => now - t < CRASH_WINDOW_MS);
        if (crashNow) crashes.push(now);
        writeList(KEY_CRASHES, crashes);

        // boot 环（紧凑循环）
        const ring = readList(KEY).filter((t) => now - t < WINDOW_MS);
        ring.push(now);
        writeList(KEY, ring.slice(-RING));

        // 分级判定（见文件头）：ring≥3 或 ≥2 次崩溃 ⇒ 2；恰 1 次崩溃 ⇒ 1。
        bootTier = ring.length >= TRIGGER || crashes.length >= 2
            ? 2
            : crashes.length >= 1 ? 1 : 0;
        // 排障锚点：日志里能直接看出该会话为何降档。
        console.info(`[stability] boot ring=${ring.length} crashes=${crashes.length} tier=${bootTier}`);
    } catch {
        /* 隐私模式/配额异常时按标准档走 */
    }
}

/** 场景级降档开关（switchTo 在 manifest 已知后设置，manifest.mobileLowSpec）。 */
export function setMobileSceneLowSpec(lowSpec: boolean): void {
    if (sceneLowSpec === lowSpec) return;
    sceneLowSpec = lowSpec;
    console.info(`[stability] scene low-spec tier=${lowSpec}`);
}

/** 当前降档级别（0 标准 / 1 降一级 / 2 全面降档）：boot 快照与场景级取大者。
 *  LOD 上限/分块并发按级别递进；预算/像素比/深度双生体等全面降档点问
 *  mobileDegradeActive（仅 tier 2）。 */
export function mobileTierLevel(): number {
    return Math.max(bootTier, sceneLowSpec ? 2 : 0);
}

/** 是否处于全面降档档位（tier 2）：像素比上限 1.0、跳过深度双生体等「重手
 * 段」只在该级启用。 */
export function mobileDegradeActive(): boolean {
    return mobileTierLevel() >= 2;
}

/** 会话稳定存活后调用：清空 boot 环并放宽 boot 级别，下次 boot 从标准档重新
 * 开始（崩溃时间戳保留至自然过期）。 */
export function markStableSession(): void {
    bootTier = 0;
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        /* 同上 */
    }
}

/** 心跳：页面存活期间定期记录（main.ts 每 5s），崩溃恢复判据「死前还在跑」。 */
export function markAlive(): void {
    try {
        sessionStorage.setItem(KEY_ALIVE, String(Date.now()));
    } catch {
        /* 同上 */
    }
}

/** 干净退出（pagehide：正常跳转/关闭/手动刷新）：与崩溃死亡区分开。 */
export function markCleanExit(): void {
    try {
        sessionStorage.setItem(KEY_CLEAN, '1');
    } catch {
        /* 同上 */
    }
}
